import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAbrirNovo } from '../hooks/useAbrirNovo';
import { Link } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuX, LuChevronLeft, LuChevronRight, LuPencil, LuEye, LuMessageCircle,
    LuDownload, LuTrash2, LuPaperclip, LuUpload, LuTriangleAlert,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection, Switch } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import ImportarPlanilhaPanel from '../components/ImportarPlanilhaPanel';
import { PERMISSOES, temPermissao } from '../config/permissions';
import OrganizationPicker from '../components/OrganizationPicker';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
import { exportarListaCsv } from '../services/exportarCsv';
import '../components/ListKit.css';
// ContactsPage.css é um dos QUATRO arquivos que declaram .management-table,
// .filters-container, .filter-input e .actions-cell — classes consumidas por
// 15 telas ainda não migradas. Esta tela não usa mais nenhuma delas, mas o
// import fica: sem ele o arquivo sai do bundle e as outras quebram.
// Ver docs/designsystem/estado-do-redesign.md.
import './ContactsPage.css';
import {
    ContactService, type Contact, type CreateContactDTO, type UpdateContactDTO, type DuplicidadeContato,
} from '../services/ContactService';
import { OpportunityService, type Opportunity } from '../services/OpportunityService';
import { AgendaService, type AgendaEvent } from '../services/AgendaService';
import { AttachmentService } from '../services/AttachmentService';
import type { ContatoAnexoResponseDTO, TipoAnexo } from '../types/Attachment';
import { mascararCep, manterSeMesmaCidade } from '../services/CepService';
import { useBuscaCep } from '../hooks/useBuscaCep';
import { nomeEtapa } from '../data/etapas';

/* ─── Regras de domínio ───────────────────────────────────────── */

const STATUS_GANHO = 3;
const STATUS_PERDIDO = 6;

const POR_PAGINA = [25, 50, 100];

type Modo = 'ver' | 'editar';

interface Rascunho {
    contatoId?: number;
    nome: string; telefone: string; email: string; segment: string;
    cep: string; street: string; number: string; complement: string;
    neighborhood: string; city: string; state: string;
    /** Vínculo com a organização. `null` é estado válido. */
    organizacaoId: number | null;
    organizacaoNome: string | null;
}

const VAZIO: Rascunho = {
    nome: '', telefone: '', email: '', segment: '',
    cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '',
    organizacaoId: null, organizacaoNome: null,
};

const paraRascunho = (c: Contact): Rascunho => ({
    contatoId: c.contatoId,
    nome: c.nome ?? '', telefone: c.telefone ?? '', email: c.email ?? '', segment: c.segment ?? '',
    cep: mascararCep(c.cep ?? ''), street: c.street ?? '', number: c.number ?? '', complement: c.complement ?? '',
    neighborhood: c.neighborhood ?? '', city: c.city ?? '', state: c.state ?? '',
    organizacaoId: c.organizacaoId ?? null, organizacaoNome: c.organizacaoNome ?? null,
});

const iniciais = (nome: string) => nome.trim().split(/\s+/).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

const cidadeUf = (c: Contact) => [c.city, c.state].filter(Boolean).join('/');

const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const brl = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Só o nome do arquivo: `caminhoAnexo` vem como caminho completo. */
const nomeArquivo = (caminho: string) => caminho.split(/[\\/]/).pop() || 'anexo';

/** Etiqueta do ícone: a extensão diz mais que o tipo genérico da API. */
const etiquetaArquivo = (a: ContatoAnexoResponseDTO) => {
    const ext = nomeArquivo(a.caminhoAnexo).split('.').pop();
    if (ext && ext.length <= 4 && ext !== a.caminhoAnexo) return ext.toUpperCase();
    return ({ image: 'IMG', audio: 'AUD', video: 'VID', document: 'DOC', documento: 'DOC', outros: 'ARQ' } as const)[a.tipoAnexo] ?? 'ARQ';
};

const tipoDoArquivo = (f: File): TipoAnexo => {
    if (f.type.startsWith('image/')) return 'image';
    if (f.type.startsWith('audio/')) return 'audio';
    if (f.type.startsWith('video/')) return 'video';
    return 'document';
};

/* ─── Filtro em pill com popover ──────────────────────────────── */

interface FiltroProps {
    rotulo: string;
    valor: string | null;
    opcoes: { valor: string; total: number }[];
    onChange: (v: string | null) => void;
}

const FiltroPill = ({ rotulo, valor, opcoes, onChange }: FiltroProps) => {
    const [aberto, setAberto] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    return (
        <div className="lk-filter" ref={ref}>
            <button
                type="button"
                className={`lk-pill ${valor ? 'is-on' : ''}`}
                aria-expanded={aberto}
                onClick={() => (valor ? onChange(null) : setAberto(v => !v))}
            >
                {valor ? <>{rotulo}: <b>{valor}</b> <LuX size={12} /></> : <>{rotulo} ▾</>}
            </button>

            {aberto && !valor && (
                <div className="lk-pop" role="menu">
                    {opcoes.length === 0
                        ? <button type="button" disabled>nada para filtrar</button>
                        : opcoes.map(o => (
                            <button key={o.valor} type="button"
                                onClick={() => { onChange(o.valor); setAberto(false); }}>
                                <span>{o.valor}</span>
                                <span className="lk-pop-count">{o.total}</span>
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
};

/* ─── Tela ────────────────────────────────────────────────────── */

const ContactsPage = () => {
    const celular = useCelular();
    const [contatos, setContatos] = useState<Contact[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    /* Importar, exportar e excluir: ações em massa/destrutivas, só com
       `contatos.gerenciar` — a API recusa importar e excluir sem a chave. */
    const podeGerenciar = temPermissao(PERMISSOES.contatosGerenciar);
    const [importando, setImportando] = useState(false);

    /* Recorte de trabalho: abrir um contato, voltar e reencontrar a mesma
       lista — busca, filtros e página onde estavam. Morre com a aba. */
    const [recorte, gravarRecorte] = useRecorte('ct:recorte', {
        busca: '', origem: null as string | null, cidade: null as string | null,
        pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [origem, setOrigem] = useState<string | null>(recorte.origem);
    const [cidade, setCidade] = useState<string | null>(recorte.cidade);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, origem, cidade, pagina, porPagina });
    }, [busca, origem, cidade, pagina, porPagina, gravarRecorte]);

    // Um painel, dois modos. Nunca dois painéis.
    const [aberto, setAberto] = useState<Contact | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});

    /* CEP completo preenche o resto — mesma regra de Organizações: número e
       complemento ficam com a pessoa, e o foco vai para o que falta. */
    const cep = useBuscaCep(e => {
        setRascunho(r => {
            const manter = manterSeMesmaCidade(e, { cidade: r.city, estado: r.state });
            return {
                ...r,
                street: e.logradouro ?? (manter ? r.street : ''),
                neighborhood: e.bairro ?? (manter ? r.neighborhood : ''),
                city: e.cidade,
                state: e.estado,
            };
        });
        return e.logradouro ? 'ct-num' : 'ct-rua';
    });

    // Histórico do contato — as duas listas que o /dashboard já carrega.
    const [oportunidades, setOportunidades] = useState<Opportunity[]>([]);
    const [agendamentos, setAgendamentos] = useState<AgendaEvent[]>([]);

    // Anexos: por contato, carregados só quando o painel abre.
    const [anexos, setAnexos] = useState<ContatoAnexoResponseDTO[]>([]);
    const [carregandoAnexos, setCarregandoAnexos] = useState(false);
    const [enviandoAnexo, setEnviandoAnexo] = useState(false);
    const inputArquivo = useRef<HTMLInputElement | null>(null);

    /* ─── Dados ──────────────────────────────────────────────── */

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const r = await ContactService.getAll();
            if (r.sucesso) setContatos(r.dados);
            else aviso.erro(r.mensagem);
        } catch (e) {
            console.error(e);
            aviso.erro('Não foi possível carregar os contatos.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    // O histórico não bloqueia a lista: falha em silêncio e o painel só
    // deixa de mostrar os blocos.
    useEffect(() => {
        let vivo = true;
        Promise.allSettled([OpportunityService.getAllOpportunities({ incluirForaDoFunil: true }), AgendaService.getAll()])
            .then(([ops, ags]) => {
                if (!vivo) return;
                if (ops.status === 'fulfilled') setOportunidades(ops.value || []);
                if (ags.status === 'fulfilled') setAgendamentos(ags.value || []);
            });
        return () => { vivo = false; };
    }, []);

    // Mudar um filtro volta para a página 1 — mas não na montagem, senão a
    // página restaurada da sessão se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, origem, cidade, porPagina]);

    /* ─── Anexos ─────────────────────────────────────────────── */

    const carregarAnexos = useCallback(async (contatoId: number) => {
        setCarregandoAnexos(true);
        try {
            setAnexos(await AttachmentService.getAttachmentsByContact(contatoId));
        } catch (e) {
            console.error(e);
            setAnexos([]);
        } finally {
            setCarregandoAnexos(false);
        }
    }, []);

    const idAberto = aberto?.contatoId;
    useEffect(() => {
        if (!idAberto) { setAnexos([]); return; }
        carregarAnexos(idAberto);
    }, [idAberto, carregarAnexos]);

    const anexar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = ev.target.files?.[0];
        ev.target.value = '';            // permite reenviar o mesmo arquivo
        if (!arquivo || !idAberto) return;
        setEnviandoAnexo(true);
        try {
            await AttachmentService.addAttachmentByContact(idAberto, arquivo, tipoDoArquivo(arquivo));
            aviso.ok('Anexo adicionado.');
            await carregarAnexos(idAberto);
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível anexar o arquivo.'));
        } finally {
            setEnviandoAnexo(false);
        }
    };

    const baixarAnexo = async (a: ContatoAnexoResponseDTO) => {
        let url: string | null = null;
        try {
            url = await AttachmentService.getAttachmentFileUrl(a.id);
            const link = document.createElement('a');
            link.href = url;
            link.download = nomeArquivo(a.caminhoAnexo);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            console.error(e);
            aviso.erro('Não foi possível baixar o anexo.');
        } finally {
            // o blob fica na memória até ser revogado
            if (url) setTimeout(() => URL.revokeObjectURL(url!), 10_000);
        }
    };

    const removerAnexo = async (a: ContatoAnexoResponseDTO) => {
        if (!window.confirm(`Remover "${nomeArquivo(a.caminhoAnexo)}"?`)) return;
        try {
            await AttachmentService.removeAttachment(a.id);
            aviso.ok('Anexo removido.');
            if (idAberto) await carregarAnexos(idAberto);
        } catch (e) {
            console.error(e);
            aviso.erro('Não foi possível remover o anexo.');
        }
    };

    /* ─── Filtros e paginação ────────────────────────────────── */

    const contar = (pegar: (c: Contact) => string) => {
        const m = new Map<string, number>();
        contatos.forEach(c => {
            const v = pegar(c);
            if (v) m.set(v, (m.get(v) ?? 0) + 1);
        });
        return [...m.entries()]
            .map(([valor, total]) => ({ valor, total }))
            .sort((a, b) => b.total - a.total || a.valor.localeCompare(b.valor, 'pt-BR'));
    };

    const opcoesOrigem = useMemo(() => contar(c => c.segment ?? ''), [contatos]);
    const opcoesCidade = useMemo(() => contar(cidadeUf), [contatos]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return contatos.filter(c => {
            if (origem && (c.segment ?? '') !== origem) return false;
            if (cidade && cidadeUf(c) !== cidade) return false;
            if (!t) return true;
            return c.nome.toLowerCase().includes(t)
                || (c.telefone ?? '').includes(busca.trim())
                || (c.email ?? '').toLowerCase().includes(t);
        });
    }, [contatos, busca, origem, cidade]);

    /* Exporta o que o filtro deixou, todas as páginas, com o cadastro inteiro —
       inclusive o endereço que a tabela não mostra. Só o administrador: é a
       base da empresa saindo do sistema, e a mesma regra do Importar ao lado. */
    const exportar = () => exportarListaCsv('contatos', filtrados, [
        { rotulo: 'Nome', valor: c => c.nome },
        { rotulo: 'Organização', valor: c => c.organizacaoNome },
        { rotulo: 'WhatsApp', valor: c => c.telefone, tipo: 'telefone' },
        { rotulo: 'E-mail', valor: c => c.email },
        { rotulo: 'Origem', valor: c => c.segment },
        { rotulo: 'CEP', valor: c => c.cep && mascararCep(c.cep) },
        { rotulo: 'Rua', valor: c => c.street },
        { rotulo: 'Número', valor: c => c.number },
        { rotulo: 'Complemento', valor: c => c.complement },
        { rotulo: 'Bairro', valor: c => c.neighborhood },
        { rotulo: 'Cidade', valor: c => c.city },
        { rotulo: 'UF', valor: c => c.state },
        { rotulo: 'Cadastro', valor: c => c.dataCriacao, tipo: 'data' },
    ]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    /* ─── Painel ─────────────────────────────────────────────── */

    /* Grava direto pelo endpoint, sem passar pelo Salvar do formulário: tirar do funil mexe
       em conversas e tarefas, não é campo do cadastro. */
    const [mudandoFunil, setMudandoFunil] = useState(false);
    const alterarFunil = async (foraDoFunil: boolean) => {
        if (!aberto?.contatoId || mudandoFunil) return;
        setMudandoFunil(true);
        try {
            await ContactService.definirFunil(aberto.contatoId, foraDoFunil);
            setAberto(c => (c ? { ...c, foraDoFunil } : c));
            setContatos(lista => lista.map(c => (c.contatoId === aberto.contatoId ? { ...c, foraDoFunil } : c)));
            aviso.ok(foraDoFunil ? 'Contato fora do funil.' : 'Contato de volta ao funil.');
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível alterar o funil do contato.'));
        } finally {
            setMudandoFunil(false);
        }
    };

    const abrirVer = (c: Contact) => { setAberto(c); setModo('ver'); setErros({}); };

    const abrirEdicao = (c: Contact) => {
        setAberto(c); setRascunho(paraRascunho(c)); setModo('editar'); setErros({}); cep.reiniciar();
    };

    /* A paleta do Ctrl+K pediu "Novo contato": abre o formulário direto. */
    useAbrirNovo(() => abrirNovo());

    const abrirNovo = () => {
        setAberto({ contatoId: 0, nome: '', telefone: '', email: null } as Contact);
        setRascunho(VAZIO); setModo('editar'); setErros({}); cep.reiniciar();
    };

    const fechar = () => { setAberto(null); setErros({}); cep.reiniciar(); };

    const ehNovo = modo === 'editar' && !rascunho.contatoId;

    /* ─── Duplicidade ────────────────────────────────────────── */

    /* Mesmo molde de Organizações: o aviso aparece enquanto digita, não no Salvar.
       A API compara o telefone com e sem o nono dígito e com e sem o 55 — a lista
       da tela não sabe fazer isso, por isso a consulta vai ao servidor. */
    const [duplicidade, setDuplicidade] = useState<DuplicidadeContato | null>(null);

    useEffect(() => {
        if (modo !== 'editar') { setDuplicidade(null); return; }
        const telefone = rascunho.telefone.replace(/\D/g, '');
        const email = rascunho.email.trim();
        if (telefone.length < 8 && !/^\S+@\S+\.\S+$/.test(email)) { setDuplicidade(null); return; }

        // Espera a digitação parar: sem isso é uma consulta por tecla.
        const id = window.setTimeout(() => {
            ContactService
                .verificarDuplicidade(telefone.length >= 8 ? telefone : '', email, rascunho.contatoId)
                .then(setDuplicidade)
                .catch(() => setDuplicidade(null));
        }, 400);
        return () => window.clearTimeout(id);
    }, [modo, rascunho.telefone, rascunho.email, rascunho.contatoId]);

    /* Editar um contato que JÁ tinha o número repetido (base antiga) não trava: a
       API só recusa quando o número muda. O aviso segue a mesma regra. */
    const telefoneOriginal = aberto?.telefone?.replace(/\D/g, '') ?? '';
    const telefoneMudou = ehNovo || rascunho.telefone.replace(/\D/g, '') !== telefoneOriginal;
    const telefoneRepetido = telefoneMudou ? duplicidade?.bloqueioPorTelefone ?? null : null;
    const emailRepetido = duplicidade?.avisoPorEmail ?? [];

    const validar = () => {
        const e: Record<string, string> = {};
        if (!rascunho.nome.trim()) e.nome = 'Informe o nome.';
        if (!rascunho.telefone.trim()) e.telefone = 'Informe o telefone.';
        if (rascunho.email.trim() && !/^\S+@\S+\.\S+$/.test(rascunho.email.trim())) e.email = 'E-mail inválido.';
        setErros(e);
        return Object.keys(e).length === 0;
    };

    const salvar = async () => {
        if (!validar()) return;
        setSalvando(true);
        try {
            const base = {
                nome: rascunho.nome.trim(),
                telefone: rascunho.telefone.trim(),
                email: rascunho.email.trim(),
                segment: rascunho.segment.trim() || null,
                cep: rascunho.cep.trim() || null,
                street: rascunho.street.trim() || null,
                number: rascunho.number.trim() || null,
                complement: rascunho.complement.trim() || null,
                neighborhood: rascunho.neighborhood.trim() || null,
                city: rascunho.city.trim() || null,
                state: rascunho.state.trim() || null,
                organizacaoId: rascunho.organizacaoId,
            };

            const r = rascunho.contatoId
                ? await ContactService.update({ ...base, contatoId: rascunho.contatoId } as UpdateContactDTO)
                : await ContactService.create(base as CreateContactDTO);

            if (r.sucesso) {
                aviso.ok(rascunho.contatoId ? 'Contato atualizado.' : 'Contato criado.');
                await carregar();
                fechar();
            } else {
                aviso.erro(r.mensagem || 'Não foi possível salvar o contato.');
            }
        } catch (e) {
            console.error(e);
            aviso.erro(textoDoErro(e, 'Não foi possível salvar o contato.'));
        } finally {
            setSalvando(false);
        }
    };

    /* `alvo` opcional porque a exclusão tem duas portas: a lixeira da linha
       (que manda o contato) e o rodapé do painel (que usa o que está em
       edição). */
    const excluir = async (alvo?: Contact) => {
        const id = alvo?.contatoId ?? rascunho.contatoId;
        const nome = alvo?.nome ?? rascunho.nome;
        if (!id) return;
        if (!window.confirm(`Excluir o contato "${nome}"? Esta ação não pode ser desfeita.`)) return;
        setSalvando(true);
        try {
            const r = await ContactService.delete(id);
            if (r.sucesso) {
                aviso.ok('Contato removido.');
                await carregar();
                fechar();
            } else {
                aviso.erro(r.mensagem || 'Não foi possível remover o contato.');
            }
        } catch (e) {
            console.error(e);
            aviso.erro('Não foi possível remover o contato.');
        } finally {
            setSalvando(false);
        }
    };

    const campo = (k: keyof Rascunho) => ({
        value: rascunho[k] as string,
        onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
            const valor = k === 'cep' ? mascararCep(ev.target.value) : ev.target.value;
            setRascunho(r => ({ ...r, [k]: valor }));
            if (k === 'cep') cep.aoDigitar(valor);
        },
    });

    /* ─── Histórico do contato aberto ────────────────────────── */

    const historico = useMemo(() => {
        if (!aberto?.contatoId) return { ops: [], ags: [] };
        const ops = oportunidades
            .filter(o => o.idContato === aberto.contatoId)
            .sort((a, b) => new Date(b.dataConversaCriada).getTime() - new Date(a.dataConversaCriada).getTime());
        const ags = agendamentos
            .filter(a => a.contatoId === aberto.contatoId)
            .sort((a, b) => new Date(b.dataInicio).getTime() - new Date(a.dataInicio).getTime());
        return { ops, ags };
    }, [aberto, oportunidades, agendamentos]);

    /* ─── Render ─────────────────────────────────────────────── */

    /* Organização entrou depois do nome: é identidade, não conteúdo, e
       fica ao lado de quem ela qualifica. As proporções encolheram para
       abrir a coluna sem espremer o e-mail. */
    const COLUNAS = '1.1fr 1fr .8fr 1.1fr .7fr .8fr .65fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {contatos.length === 0 ? 'Nenhum contato cadastrado' : 'Nada com esses filtros'}
            </div>
            <p className="lk-blank-text">
                {contatos.length === 0
                    ? (podeGerenciar
                        ? 'Os contatos aparecem aqui conforme entram pelo WhatsApp, ou você cadastra manualmente ou importa de uma planilha.'
                        : 'Os contatos aparecem aqui conforme entram pelo WhatsApp, ou você cadastra manualmente.')
                    : 'Ajuste a busca ou limpe os filtros para ver os outros registros.'}
            </p>
            {contatos.length === 0
                ? <div className="ip-blank-actions">
                    {podeGerenciar && (
                        <button type="button" className="lk-btn" onClick={() => setImportando(true)}>
                            <LuUpload size={14} /> Importar planilha
                        </button>
                    )}
                    <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                        <LuPlus size={14} /> Novo contato
                    </button>
                  </div>
                : <button type="button" className="lk-btn"
                    onClick={() => { setBusca(''); setOrigem(null); setCidade(null); }}>
                    Limpar filtros
                  </button>}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />

            <span className="lk-range">{inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}</span>

            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Contatos"
                subtitle="Quem já falou com a sua operação"
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && contatos.length === 0 ? undefined :
                    <div className="lk-actions">
                        {podeGerenciar && (
                            <button type="button" className="lk-btn" onClick={() => setImportando(true)}>
                                <LuUpload size={14} /> Importar planilha
                            </button>
                        )}
                        {podeGerenciar && (
                            <button type="button" className="lk-btn" onClick={exportar} disabled={isLoading}>
                                <LuDownload size={14} /> Exportar
                            </button>
                        )}
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo contato
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input
                            type="search"
                            placeholder="Buscar por nome, telefone ou e-mail…"
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                        />
                    </label>

                    <FiltroPill rotulo="Origem" valor={origem} opcoes={opcoesOrigem} onChange={setOrigem} />
                    <FiltroPill rotulo="Cidade" valor={cidade} opcoes={opcoesCidade} onChange={setCidade} />

                    <span className="lk-count">
                        {contatos.length} {contatos.length === 1 ? 'contato' : 'contatos'}
                        {filtrados.length !== contatos.length && ` · ${filtrados.length} no filtro`}
                    </span>
                </div>

                {/* Celular: a grade de 860px não cabe em 358, então a linha vira
                    cartão — nome, organização e o contato em si. E-mail, cadastro e
                    o resto ficam no painel, um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(c => (
                                    <ListCard
                                        key={c.contatoId}
                                        avatar={<span className="lk-ava" aria-hidden="true">{iniciais(c.nome)}</span>}
                                        titulo={c.nome}
                                        apoio={c.organizacaoNome || 'sem organização'}
                                        estado={
                                            <>
                                                <span className="lk-wa"><i />{c.telefone || 'sem telefone'}</span>
                                                {c.segment && <span className="lk-tag">{c.segment}</span>}
                                                {cidadeUf(c) && <span>· {cidadeUf(c)}</span>}
                                            </>
                                        }
                                        selected={aberto?.contatoId === c.contatoId}
                                        label={`Abrir ${c.nome}`}
                                        onOpen={() => abrirVer(c)}
                                        acoes={podeGerenciar ? (
                                            <RowAction icon={<LuTrash2 size={15} />} label="Excluir contato" danger
                                                onClick={() => excluir(c)} />
                                        ) : undefined}
                                    />
                                ))}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Contato</div>
                            <div className="lk-head">Organização</div>
                            <div className="lk-head">WhatsApp</div>
                            <div className="lk-head">E-mail</div>
                            <div className="lk-head">Origem</div>
                            <div className="lk-head">Cidade/UF</div>
                            <div className="lk-head">Cadastro</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>

                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(c => (
                                    <ListRow
                                        key={c.contatoId}
                                        columns={COLUNAS}
                                        selected={aberto?.contatoId === c.contatoId}
                                    >
                                        <span className="lk-cell lk-strong"><span>{c.nome}</span></span>
                                        <span className={`lk-cell ${c.organizacaoNome ? 'lk-muted' : 'lk-empty-value'}`}>
                                            <span>{c.organizacaoNome || 'sem organização'}</span>
                                        </span>
                                        <span className="lk-cell lk-muted">
                                            <span className="lk-wa"><i />{c.telefone || '—'}</span>
                                        </span>
                                        <span className={`lk-cell ${c.email ? 'lk-muted' : 'lk-empty-value'}`}>
                                            <span>{c.email || 'sem e-mail'}</span>
                                        </span>
                                        <span className="lk-cell">
                                            {c.segment
                                                ? <span className="lk-tag">{c.segment}</span>
                                                : <span className="lk-empty-value">—</span>}
                                        </span>
                                        <span className="lk-cell lk-muted"><span>{cidadeUf(c) || '—'}</span></span>
                                        <span className="lk-cell lk-muted"><span>{dataCurta(c.dataCriacao)}</span></span>
                                        <RowActions>
                                            <RowAction icon={<LuEye size={14} />} label="Ver contato"
                                                onClick={() => abrirVer(c)} />
                                            {podeGerenciar && (
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir contato" danger
                                                    onClick={() => excluir(c)} />
                                            )}
                                        </RowActions>
                                    </ListRow>
                                ))}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}
                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            {/* ─── O painel: um só, dois modos ─── */}
            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                initials={iniciais(modo === 'editar' ? (rascunho.nome || 'Novo') : (aberto?.nome ?? ''))}
                title={modo === 'editar'
                    ? (rascunho.nome.trim() || (ehNovo ? 'Novo contato' : 'Contato'))
                    : (aberto?.nome ?? '')}
                subtitle={aberto?.dataCriacao ? `Contato · cadastrado em ${dataCurta(aberto.dataCriacao)}` : 'Contato'}
                editing={modo === 'editar'}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                actions={aberto && (
                    <>
                        <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                            <LuPencil size={13} /> Editar
                        </button>
                        {aberto.telefone && (
                            <a
                                className="rp-btn is-wa"
                                href={`https://wa.me/${aberto.telefone.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                <LuMessageCircle size={13} /> WhatsApp
                            </a>
                        )}
                    </>
                )}
                footer={
                    <>
                        {!ehNovo && podeGerenciar && (
                            <button type="button" className="rp-btn is-danger" onClick={() => excluir()} disabled={salvando}>
                                Excluir contato
                            </button>
                        )}
                        <button type="button" className="rp-btn"
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))} disabled={salvando}>
                            Cancelar
                        </button>
                        <button type="button" className="rp-btn is-primary" onClick={salvar}
                            disabled={salvando || !!telefoneRepetido}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </>
                }
            >
                {modo === 'ver' && aberto && (
                    <>
                        <PanelSection title="Contato">
                            <div className="rp-kv">
                                <span className="rp-k">WhatsApp</span>
                                <span className="rp-v">{aberto.telefone || <i className="rp-v is-empty">não informado</i>}</span>
                                <span className="rp-k">E-mail</span>
                                {aberto.email
                                    ? <span className="rp-v">{aberto.email}</span>
                                    : <span className="rp-v is-empty">não informado</span>}
                                <span className="rp-k">Organização</span>
                                {aberto.organizacaoNome
                                    ? <span className="rp-v">
                                        <Link className="rp-link-btn" to={`/organizations?abrir=${aberto.organizacaoId}`}>
                                            {aberto.organizacaoNome}
                                        </Link>
                                      </span>
                                    : <span className="rp-v is-empty">sem organização</span>}
                                <span className="rp-k">Origem</span>
                                {aberto.segment
                                    ? <span className="rp-v"><span className="rp-tag">{aberto.segment}</span></span>
                                    : <span className="rp-v is-empty">não informada</span>}
                            </div>
                            <div className="rp-toggle-row">
                                <div className="rp-toggle-text">
                                    <span className="rp-toggle-label">Fora do funil</span>
                                    <span className="rp-toggle-hint">
                                        Fornecedor ou contato pessoal. As conversas continuam no Chat, mas não contam
                                        na Pipeline nem nas métricas, e o robô não atende.
                                    </span>
                                </div>
                                <Switch
                                    checked={aberto.foraDoFunil === true}
                                    label="Fora do funil"
                                    disabled={mudandoFunil}
                                    onChange={alterarFunil}
                                />
                            </div>
                        </PanelSection>

                        <PanelSection title="Endereço">
                            {aberto.street || aberto.city ? (
                                <div className="rp-kv">
                                    <span className="rp-k">Logradouro</span>
                                    <span className="rp-v">
                                        {[aberto.street, aberto.number].filter(Boolean).join(', ')}
                                        {aberto.complement ? ` — ${aberto.complement}` : ''}
                                    </span>
                                    <span className="rp-k">Bairro</span>
                                    <span className="rp-v">{aberto.neighborhood || '—'}</span>
                                    <span className="rp-k">Cidade</span>
                                    <span className="rp-v">
                                        {cidadeUf(aberto) || '—'}{aberto.cep ? ` · ${mascararCep(aberto.cep)}` : ''}
                                    </span>
                                </div>
                            ) : <p className="rp-none">Sem endereço cadastrado.</p>}
                        </PanelSection>

                        <PanelSection
                            title={aberto.foraDoFunil
                                ? `Conversas fora do funil · ${historico.ops.length}`
                                : `Oportunidades · ${historico.ops.length}`}
                            extra={aberto.foraDoFunil ? <span className="rp-tag">Fora do funil</span> : undefined}
                        >
                            {historico.ops.length === 0
                                ? <p className="rp-none">{aberto.foraDoFunil
                                    ? 'Nenhuma conversa para este contato.'
                                    : 'Nenhuma oportunidade para este contato.'}</p>
                                : historico.ops.slice(0, 5).map(o => (
                                    <div className="rp-link" key={o.idConversa}>
                                        <div>
                                            {/* Não existe título de oportunidade no modelo: `nomeContato` é o
                                                nome do próprio contato, que aqui seria repetido em toda linha.
                                                O que identifica é quando entrou e quem atende. */}
                                            <div className="rp-link-title">
                                                {o.nomeCampanha || `Aberta em ${dataCurta(o.dataConversaCriada)}`}
                                            </div>
                                            <div className="rp-link-meta">{o.nomeUsuarioResponsavel || 'sem responsável'}</div>
                                        </div>
                                        <div className="rp-link-right">
                                            <span className={`rp-badge ${o.idStauts === STATUS_GANHO ? 'is-good'
                                                : o.idStauts === STATUS_PERDIDO ? 'is-lost' : 'is-open'}`}>
                                                {o.idStauts ? nomeEtapa(o.idStauts) : o.descricaoStatus}
                                            </span>
                                            <div className="rp-link-meta">{brl(o.valor || 0)}</div>
                                        </div>
                                    </div>
                                ))}
                        </PanelSection>

                        <PanelSection title={`Agenda · ${historico.ags.length}`}>
                            {historico.ags.length === 0
                                ? <p className="rp-none">Nenhum agendamento para este contato.</p>
                                : historico.ags.slice(0, 5).map(a => {
                                    const d = new Date(a.dataInicio);
                                    const futuro = d.getTime() > Date.now();
                                    return (
                                        <div className="rp-link" key={a.agendaId}>
                                            <div>
                                                <div className="rp-link-title">{a.descricao}</div>
                                                <div className="rp-link-meta">
                                                    {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                                    {', '}{d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                            <div className="rp-link-right">{futuro ? 'a seguir' : 'realizado'}</div>
                                        </div>
                                    );
                                })}
                        </PanelSection>

                        {/* O botão de anexar aparece SEMPRE, mesmo sem nenhum arquivo:
                            sem ele não há como começar. */}
                        <PanelSection title={`Anexos${anexos.length ? ` · ${anexos.length}` : ''}`}>
                            {carregandoAnexos
                                ? <p className="rp-none">Carregando…</p>
                                : anexos.length === 0
                                    ? <p className="rp-none">Nenhum arquivo anexado.</p>
                                    : anexos.map(a => (
                                        <div className="rp-file" key={a.id}>
                                            <span className="rp-file-ico">{etiquetaArquivo(a)}</span>
                                            <span className="rp-file-name" title={nomeArquivo(a.caminhoAnexo)}>
                                                {nomeArquivo(a.caminhoAnexo)}
                                            </span>
                                            <button type="button" className="rp-file-act"
                                                onClick={() => baixarAnexo(a)} aria-label="Baixar">
                                                <LuDownload size={13} />
                                            </button>
                                            <button type="button" className="rp-file-act is-danger"
                                                onClick={() => removerAnexo(a)} aria-label="Remover">
                                                <LuTrash2 size={13} />
                                            </button>
                                        </div>
                                    ))}

                            <input
                                ref={inputArquivo}
                                type="file"
                                hidden
                                onChange={anexar}
                            />
                            <button
                                type="button"
                                className="rp-add"
                                onClick={() => inputArquivo.current?.click()}
                                disabled={enviandoAnexo || !aberto.contatoId}
                            >
                                <LuPaperclip size={13} />
                                {enviandoAnexo ? 'Enviando…' : 'Anexar arquivo'}
                            </button>
                        </PanelSection>
                    </>
                )}

                {modo === 'editar' && (
                    <>
                        <PanelSection title="Contato">
                            <div className="rp-field">
                                <label className="rp-label" htmlFor="ct-nome">Nome</label>
                                <input id="ct-nome" className={`rp-input ${erros.nome ? 'has-error' : ''}`}
                                    autoFocus {...campo('nome')} />
                                {erros.nome && <p className="rp-error">{erros.nome}</p>}
                            </div>
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-tel">WhatsApp</label>
                                    <input id="ct-tel" className={`rp-input ${erros.telefone || telefoneRepetido ? 'has-error' : ''}`}
                                        {...campo('telefone')} />
                                    {erros.telefone && <p className="rp-error">{erros.telefone}</p>}
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-org">Origem</label>
                                    <input id="ct-org" className="rp-input" placeholder="Ex: Varejo" {...campo('segment')} />
                                </div>
                            </div>

                            {/* Telefone repetido BLOQUEIA: é por ele que a conversa do WhatsApp acha o
                                contato, e dois com o mesmo número dividiriam o histórico. A API recusa
                                de qualquer forma; o aviso só chega antes do Salvar. */}
                            {telefoneRepetido && (
                                <div className="cdup is-erro">
                                    <LuTriangleAlert size={13} />
                                    <div>
                                        Este número já é de <b>{telefoneRepetido.nome}</b>.
                                        Dois contatos não podem ter o mesmo WhatsApp.
                                        <div className="cdup-acts">
                                            <button type="button" className="cdup-b is-primary"
                                                onClick={() => { fechar(); abrirVer(telefoneRepetido); }}>
                                                Abrir o existente
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="ct-mail">E-mail</label>
                                <input id="ct-mail" type="email" className={`rp-input ${erros.email ? 'has-error' : ''}`}
                                    placeholder="nome@empresa.com.br" {...campo('email')} />
                                {erros.email && <p className="rp-error">{erros.email}</p>}

                                {/* E-mail repetido só avisa: e-mail de família ou da empresa se repete
                                    entre pessoas diferentes. */}
                                {!telefoneRepetido && emailRepetido.length > 0 && (
                                    <div className="cdup">
                                        <LuTriangleAlert size={13} />
                                        <div>
                                            <b>{emailRepetido[0].nome}</b> já usa este e-mail
                                            {emailRepetido.length > 1 && ` (e mais ${emailRepetido.length - 1})`}. É a mesma pessoa?
                                            <div className="cdup-acts">
                                                <button type="button" className="cdup-b is-primary"
                                                    onClick={() => { const alvo = emailRepetido[0]; fechar(); abrirVer(alvo); }}>
                                                    Abrir o existente
                                                </button>
                                                <span className="cdup-nota">ou continue — o cadastro segue liberado</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="rp-field">
                                <label className="rp-label" htmlFor="ct-org-pick">Organização</label>
                                <OrganizationPicker
                                    id="ct-org-pick"
                                    value={rascunho.organizacaoId}
                                    valueLabel={rascunho.organizacaoNome}
                                    onChange={(id, nome) => setRascunho(r => ({ ...r, organizacaoId: id, organizacaoNome: nome }))}
                                />
                            </div>
                        </PanelSection>

                        <PanelSection title="Endereço">
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-cep">CEP</label>
                                    <input id="ct-cep" className={`rp-input ${cep.aviso?.erro ? 'has-error' : ''}`}
                                        placeholder="00000-000" inputMode="numeric" maxLength={9} {...campo('cep')} />
                                    {cep.aviso && <p className={cep.aviso.erro ? 'rp-error' : 'rp-hint'}>{cep.aviso.texto}</p>}
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-bairro">Bairro</label>
                                    <input id="ct-bairro" className="rp-input is-auto" disabled={cep.travado('bairro')} {...campo('neighborhood')} />
                                </div>
                            </div>
                            <div className="rp-three">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-rua">Rua</label>
                                    <input id="ct-rua" className="rp-input is-auto" disabled={cep.travado('logradouro')} {...campo('street')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-compl">Complemento</label>
                                    <input id="ct-compl" className="rp-input" {...campo('complement')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-num">Nº</label>
                                    <input id="ct-num" className="rp-input" {...campo('number')} />
                                </div>
                            </div>
                            <div className="rp-two">
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-cidade">Cidade</label>
                                    <input id="ct-cidade" className="rp-input is-auto" disabled={cep.travado('cidade')} {...campo('city')} />
                                </div>
                                <div className="rp-field">
                                    <label className="rp-label" htmlFor="ct-uf">UF</label>
                                    <input id="ct-uf" className="rp-input is-auto" maxLength={2} disabled={cep.travado('estado')} {...campo('state')} />
                                </div>
                            </div>
                        </PanelSection>
                    </>
                )}

                {/* Histórico (fase 2): seção no fim do modo ver, sem abas — o Editar fica onde está.
                    Junta o do contato com o das oportunidades e organizações dele. */}
                {modo === 'ver' && aberto && (
                    <SecaoHistorico entidade="contato" id={aberto.contatoId} />
                )}
            </RecordPanel>

            {podeGerenciar && (
                <ImportarPlanilhaPanel
                    open={importando}
                    onClose={() => setImportando(false)}
                    contexto="contatos"
                    onConcluido={carregar}
                />
            )}
        </div>
    );
};

export default ContactsPage;
